import { useIsMounted } from '@gogame/hooks-ts';
import { useMutation } from '@gogame/query';
import { useScreenMessage } from '@gogame/screens';

import { loadToken } from 'scenes/Gallery/GalleryPage/sources/token';

import { upload } from '../queryKeyFactory';

const chunkSize = 20 * 1024 * 1024; //20 MB

export const useUpload = ({ source, onProgress }) => {
  const { showErrorMessage } = useScreenMessage();
  const isMounted = useIsMounted();

  const uploadFile = async ({ file }) => {
    const numberOfChunks = Math.ceil(file.size / chunkSize);

    const token = await loadToken();
    let retryCount = 5;
    let guid;

    for (let i = 0; i < numberOfChunks; i++) {
      const chunkStart = i * chunkSize;
      const chunkEnd = Math.min((i + 1) * chunkSize, file.size);

      const chunk = file.slice(chunkStart, chunkEnd, file.type);

      try {
        const headers = {
          'content-range': `bytes ${chunkStart}-${chunkEnd - 1}/${file.size}`,
          'x-token': token,
        };

        if (guid) {
          headers['x-guid'] = guid;
        }
        const chunkForm = new FormData();
        chunkForm.append('file', chunk, file.name);
        onProgress(Math.ceil((chunkStart / file.size) * 100));
        guid = await source(chunkForm, {
          headers,
        });
        if (!isMounted()) {
          return;
        }
        retryCount = 5;
        onProgress(Math.ceil((chunkEnd / file.size) * 100));
      } catch (e) {
        const serveError = e?.message;
        if (serveError) {
          onProgress(-1);
          showErrorMessage(serveError);
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 2000));
        retryCount--;
        if (retryCount > 0) {
          i--;
        } else {
          onProgress(-1);
          showErrorMessage(`Upload ${file.name} Failed`);
          return;
        }
      }
    }
    return guid;
  };

  const mutation = useMutation(upload(), uploadFile, { refresh: false });

  return [mutation.mutateAsync, mutation];
};
